import { computed, ref } from 'vue';

import type { ArticleContent } from '@/features/articleContent';
import { getAttractionBySlug } from '@/shared/api/experience';
import type { AttractionCardResponse } from '@/shared/api/experience';
import { useLogger } from '@/shared/utils/logger';

import { mapAttractionCard } from '../lib/mapAttractionCard';

import type { RouteLocationNormalizedLoaded } from 'vue-router';

export const useAttractionPageData = (route: RouteLocationNormalizedLoaded) => {
  const logger = useLogger();

  const data = ref<AttractionCardResponse | null>(null);
  // страница стартует со скелетоном, до первого ответа BFF показывать нечего
  const isLoading = ref(true);
  // любой ответ BFF, кроме успешного, приводит к блоку «страница не найдена»
  const hasError = ref(false);

  const getAttraction = async () => {
    const { slug } = route.params as { slug: string };

    if (!slug) {
      isLoading.value = false;
      hasError.value = true;
      return;
    }

    isLoading.value = true;
    hasError.value = false;

    try {
      const { data: response, error } = await getAttractionBySlug({ path: { slug } });

      if (error || !response) {
        logger.error(`Не удалось загрузить достопримечательность ${slug}`, error);
        hasError.value = true;
        return;
      }

      if (response.redirect) {
        window.location.href = response.redirect;
        return;
      }

      data.value = response;
    } finally {
      isLoading.value = false;
    }
  };

  const card = computed(() => (data.value ? mapAttractionCard(data.value) : null));
  const seo = computed(() => data.value?.metatags);
  const canonicalUrl = computed(() => seo.value?.canonical_url);
  const descriptionTokens = computed(() => (data.value?.description_tokens ?? []) as unknown as ArticleContent);
  const description = computed(() => ({
    id: card.value?.id ?? 0,
    blocks: descriptionTokens.value,
  }));

  return {
    data,
    isLoading,
    hasError,
    getAttraction,
    card,
    seo,
    canonicalUrl,
    description,
  };
};
